import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

interface ExerciseInstruction {
  id?: string;
  memberId: string;
  exerciseId: string;
  exerciseName: string;
  customInstructions: string;
  createdAt?: string;
  updatedAt?: string;
  lastAssignedDate?: string;
  totalAssignments?: number;
  // Additional fields from the new API response
  instructions?: string[];
  benefits?: string[];
  safetyTips?: string[];
  muscleGroup?: string;
  difficultyLevel?: string;
  equipmentNeeded?: string[];
  estimatedCaloriesBurned?: number;
  calorieUnit?: string;
  diabeticGuidance?: string;
  riskAssessment?: string;
}

interface DialogData {
  exercise: ExerciseInstruction;
  memberName: string;
}

@Component({
  selector: 'app-edit-instruction-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    DragDropModule,
  ],
  templateUrl: './edit-instruction-dialog.component.html',
  styleUrl: './edit-instruction-dialog.component.css',
})
export class EditInstructionDialogComponent {
  exercise: ExerciseInstruction;
  memberName: string;
  originalInstructions: string;
  customInstructionsList: string[] = [''];

  constructor(
    public dialogRef: MatDialogRef<EditInstructionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.exercise = { ...data.exercise };
    this.memberName = data.memberName;

    // Ensure customInstructions is a string
    if (!this.exercise.customInstructions) {
      this.exercise.customInstructions = '';
    }

    this.originalInstructions = this.exercise.customInstructions;

    // Debug logging
    // console.log('Dialog data:', data);
    // console.log('Exercise:', this.exercise);
    // console.log(
    //   'Dialog memberName:', this.memberName,
    //   'Exercise ID:', this.exercise.exerciseId
    // );
    // console.log('Original instructions:', this.originalInstructions);

    // Initialize custom instructions list based on existing content
    this.initializeInstructionsList();

    // Debug logging after initialization
    // console.log('Initialized instructions list:', this.customInstructionsList);
  }

  private initializeInstructionsList(): void {
    // console.log('=== Initializing instructions list ===');
    // console.log(
    //   'Instructions from API:',
    //   this.exercise.instructions
    // );
    // console.log('Default instructions:', this.exercise.instructions);

    let initialList: string[] = [];

    // First priority: Use existing custom instructions if they exist
    if (
      this.exercise.customInstructions &&
      typeof this.exercise.customInstructions === 'string' &&
      this.exercise.customInstructions.trim()
    ) {
      // console.log('Found existing custom instructions, using them');
      initialList = this.parseInstructionsFromText(
        this.exercise.customInstructions.trim()
      );
    }
    // Second priority: Use default exercise instructions if no custom instructions exist
    else if (
      this.exercise.instructions &&
      Array.isArray(this.exercise.instructions) &&
      this.exercise.instructions.length > 0
    ) {
      // console.log(
      //   'No custom instructions, using default exercise instructions'
      // );
      initialList = [...this.exercise.instructions];
    }
    // Fallback: Empty field if neither exist
    else {
      // console.log('No instructions found, using empty field');
      initialList = [''];
    }

    // Ensure we have at least one field
    if (initialList.length === 0) {
      // console.log('No instructions parsed, adding empty field');
      initialList = [''];
    }

    // Set the list only once
    this.customInstructionsList = initialList;
    // console.log('Final instructions list:', this.customInstructionsList);
  }

  private parseInstructionsFromText(text: string): string[] {
    // Split by newlines and filter out empty lines
    const lines = text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    // console.log('Split lines:', lines);

    if (lines.length === 0) {
      // console.log('No valid lines found, using empty field');
      return [''];
    } else if (lines.length === 1) {
      // Single line - check if it's numbered/bulleted
      const line = lines[0];
      if (/^\d+\.\s*/.test(line)) {
        // Numbered list item
        const cleaned = line.replace(/^\d+\.\s*/, '').trim();
        // console.log('Single numbered item:', cleaned);
        return [cleaned];
      } else if (/^[-•*]\s*/.test(line)) {
        // Bulleted list item
        const cleaned = line.replace(/^[-•*]\s*/, '').trim();
        // console.log('Single bulleted item:', cleaned);
        return [cleaned];
      } else {
        // Plain text
        // console.log('Single plain text:', line);
        return [line];
      }
    } else {
      // Multiple lines
      // console.log('Processing multiple lines:', lines.length);

      // Check if all lines are numbered
      const allNumbered = lines.every((line) => /^\d+\.\s*/.test(line));
      const allBulleted = lines.every((line) => /^[-•*]\s*/.test(line));

      if (allNumbered) {
        // console.log('All lines are numbered');
        return lines
          .map((line) => line.replace(/^\d+\.\s*/, '').trim())
          .filter((item) => item.length > 0);
      } else if (allBulleted) {
        // console.log('All lines are bulleted');
        return lines
          .map((line) => line.replace(/^[-•*]\s*/, '').trim())
          .filter((item) => item.length > 0);
      } else {
        // console.log('Mixed or plain lines');
        // Use lines as they are
        return lines;
      }
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    // Filter out empty instructions and trim whitespace
    const validInstructions = this.customInstructionsList
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    // Update the exercise data with both formats for compatibility
    this.exercise.customInstructions = validInstructions
      .map((item, index) => `${index + 1}. ${item}`)
      .join('\n');

    // Also add the array format for the API
    (this.exercise as any).instructionsArray = validInstructions;

    this.dialogRef.close(this.exercise);
  }

  addInstruction(): void {
    if (this.customInstructionsList.length < 10) {
      // Create a new array instead of mutating the existing one
      this.customInstructionsList = [...this.customInstructionsList, ''];
    }
  }

  removeInstruction(index: number): void {
    if (this.customInstructionsList.length > 1) {
      // Create a new array instead of mutating the existing one
      this.customInstructionsList = this.customInstructionsList.filter(
        (_, i) => i !== index
      );
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  onDrop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(
      this.customInstructionsList,
      event.previousIndex,
      event.currentIndex
    );
  }
}
